// useAutosave -- the per-editor hook behind the app's no-Save-button model.
//
// Owns the editable form state for one record and pushes every valid change
// through the shared SaveQueue:
//
//   * text/number edits debounce (~1.5s) and flush on blur/unmount;
//   * discrete controls (dates, checkboxes, selects) pass
//     `{ immediate: true }` and save right away;
//   * invalid states (e.g. a cleared required name) are HELD -- nothing is
//     enqueued, any pending stale payload is cancelled, and `fieldErrors`
//     drives inline field feedback until the user fixes it.
//
// Payloads are built AT RUN TIME (when the queue actually executes the save),
// from the record's edit session -- the latest form state plus the baseline,
// i.e. the last server-acknowledged copy. Late building means a save that was
// debouncing while something else changed the record (a quick-set button, a
// still-typing user) always writes the newest state instead of a stale
// snapshot. The default payload is buildEditedFields' full output -- the
// byte-identical shape the Save buttons used to send, which the Database
// helpers' validation expects -- and a save whose state no longer differs
// from the baseline is skipped entirely.
//
// The edit session is swapped out whenever a *different* record id loads into
// the still-mounted editor, so a pending save for the old record keeps its own
// state/baseline and can't read the new record's.
//
// Note: saves outlive the component. The queue is module-level, so a save
// started here keeps running (and reporting to the global SaveStatusPill)
// after unmount; `onSaved` side effects that setState on an unmounted
// component are a no-op in React 18.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { saveQueue, DEFAULT_DEBOUNCE_MS } from './SaveQueue.mjs';
import { buildEditedFields } from './EditedFields.mjs';

const NO_ERRORS = {};

// Does any key in `payload` differ from the baseline? Used to skip saves
// whose edits were undone (or already persisted by an earlier flush).
function differsFromBaseline(payload, baseline) {
  if (!baseline) return true;
  for (const key of Object.keys(payload)) {
    const baseValue = baseline[key] === undefined ? null : baseline[key];
    if (!Object.is(baseValue, payload[key])) return true;
  }
  return false;
}

export function useAutosave({
  recordKey,
  original,
  fields,
  buildPayload, // optional (formState, baseline) => payload; overrides buildEditedFields
  validate, // optional (formState) => { ok: true } | { ok: false, fieldErrors }
  save, // async (payload) => saved (server copy, if the caller has one)
  onSaved, // optional (saved, payload) => void, runs per successful flush
  deriveState, // optional (previous, next) => state, same contract as EditForm
  debounceMs = DEFAULT_DEBOUNCE_MS,
}) {
  const [formState, setFormStateRaw] = useState(original);
  const [fieldErrors, setFieldErrors] = useState(NO_ERRORS);

  // Latest-value refs so scheduled jobs and callbacks never go stale.
  const latest = useRef({});
  latest.current = { recordKey, fields, buildPayload, validate, save, onSaved, deriveState, debounceMs };

  // The edit session: one mutable { state, baseline } box per loaded record.
  // Scheduled jobs capture the box, not its contents, so they always save the
  // record's newest state -- and a record swap (below) gives the new record a
  // fresh box the old jobs can't see.
  const sessionRef = useRef({ state: original, baseline: original });

  // Resync when a *different* record is loaded into a still-mounted editor.
  // Keyed on the stable id (not object identity) so a parent re-passing the
  // same record as a fresh object (e.g. after a refetch) doesn't clobber
  // in-progress edits.
  const loadedIdRef = useRef(original ? original.id : undefined);
  const currentId = original ? original.id : undefined;
  if (currentId !== loadedIdRef.current) {
    loadedIdRef.current = currentId;
    sessionRef.current = { state: original, baseline: original };
    setFormStateRaw(original);
    setFieldErrors(NO_ERRORS);
  }

  // Flush pending work when the record key changes or the editor unmounts
  // (modal close, route change). The queue keeps the save alive after us.
  const keyRef = useRef(recordKey);
  keyRef.current = recordKey;
  useEffect(() => {
    const previousKey = recordKey;
    return () => saveQueue.flush(previousKey);
  }, [recordKey]);

  const scheduleSave = useCallback((immediate) => {
    const { recordKey: key } = latest.current;
    const session = sessionRef.current;

    // Payload and skip-check are evaluated fresh each time in case the
    // session moved on while this job waited in the queue.
    const buildNow = () => {
      const { fields: currentFields, buildPayload: build } = latest.current;
      return build
        ? build(session.state, session.baseline)
        : buildEditedFields(currentFields, session.state, session.baseline);
    };
    const shouldSkip = (payload) => {
      if (!payload || Object.keys(payload).length === 0) return true;
      // Callers supplying buildPayload own their payload entirely -- no
      // second-guessing (some deliberately send the full record).
      if (latest.current.buildPayload) return false;
      return !differsFromBaseline(payload, session.baseline);
    };

    // Nothing differs right now (e.g. the edit was undone within the
    // debounce window)? Drop any stale pending payload and don't enqueue.
    // Callers supplying buildPayload are exempt from the skip check -- and
    // their builder must NOT be invoked here: building can carry caller
    // bookkeeping (e.g. Trip.jsx records which route generation a payload
    // carries), which must only happen for payloads that actually run. A
    // schedule-time pre-build while an older save is in flight would credit
    // that older save with data it never sent.
    if (!latest.current.buildPayload && shouldSkip(buildNow())) {
      saveQueue.cancelPending(key);
      return;
    }

    let sentPayload = null;
    saveQueue.schedule(
      key,
      {
        run: () => {
          const payload = buildNow();
          if (shouldSkip(payload)) {
            sentPayload = null;
            return Promise.resolve();
          }
          sentPayload = payload;
          return latest.current.save(payload);
        },
        onSaved: (saved) => {
          if (sentPayload == null) return; // skipped -- nothing changed
          const serverCopy = saved && typeof saved === 'object' ? saved : null;
          session.baseline = { ...(session.baseline || {}), ...sentPayload, ...(serverCopy || {}) };
          if (latest.current.onSaved) latest.current.onSaved(saved, sentPayload);
        },
      },
      { delayMs: immediate ? 0 : latest.current.debounceMs }
    );
  }, []);

  // `next` may be an updater, `(current) => next`, and should be whenever a
  // handler writes twice in one tick: the `formState` a component closed over
  // is last render's, so a second plain-value call in the same event would
  // silently undo the first. The updater reads the edit session, which is
  // always current.
  const setFormState = useCallback((next, meta = {}) => {
    const { validate: check, recordKey: key } = latest.current;
    const session = sessionRef.current;
    const derive = latest.current.deriveState;
    const value = typeof next === 'function' ? next(session.state) : next;
    const derived = derive ? derive(session.state, value) : value;
    session.state = derived;
    setFormStateRaw(derived);

    const result = check ? check(derived) : null;
    if (result && result.ok === false) {
      setFieldErrors(result.fieldErrors || NO_ERRORS);
      saveQueue.cancelPending(key);
      return;
    }
    setFieldErrors((previous) => (previous === NO_ERRORS ? previous : NO_ERRORS));
    scheduleSave(meta.immediate === true);
  }, [scheduleSave]);

  // Adopt a save that went through the queue outside this hook (e.g. a
  // quick-set button that patches the record directly): advance the baseline
  // and mirror the saved keys into the form without disturbing other
  // still-pending edits.
  const applyExternalSave = useCallback((saved) => {
    if (!saved || typeof saved !== 'object') return;
    const session = sessionRef.current;
    session.baseline = { ...(session.baseline || {}), ...saved };
    session.state = { ...(session.state || {}), ...saved };
    setFormStateRaw(session.state);
    // The adopted values can resolve a held-invalid field (an import
    // restoring a name the user had cleared, say), so re-check rather than
    // letting a stale inline error outlive what caused it.
    const check = latest.current.validate;
    const result = check ? check(session.state) : null;
    setFieldErrors(result && result.ok === false ? result.fieldErrors || NO_ERRORS : NO_ERRORS);
  }, []);

  const flush = useCallback(() => saveQueue.flush(keyRef.current), []);
  const retry = useCallback(() => saveQueue.retry(keyRef.current), []);

  const subscribe = useCallback((listener) => saveQueue.subscribe(listener), []);
  const getRecordStatus = useCallback(() => saveQueue.getRecordStatus(keyRef.current), []);
  const queueStatus = useSyncExternalStore(subscribe, getRecordStatus);

  let status = 'clean';
  if (fieldErrors !== NO_ERRORS && Object.keys(fieldErrors).length > 0) status = 'invalid';
  else if (queueStatus === 'error') status = 'error';
  else if (queueStatus === 'saving') status = 'saving';
  else if (queueStatus === 'debouncing') status = 'dirty';
  else if (queueStatus === 'saved') status = 'saved';

  return {
    // The lane this controller writes on. Exposed so a form handed a shared
    // controller cancels/flushes the lane the controller actually uses rather
    // than re-deriving one from its own props (see EditForm's `autosave`).
    recordKey,
    formState,
    setFormState, // (next, { immediate }) -- immediate for discrete controls
    fieldErrors,
    status, // 'clean' | 'dirty' | 'invalid' | 'saving' | 'saved' | 'error'
    flush,
    retry,
    applyExternalSave,
    // Only edits the user could actually lose warrant a navigation guard:
    // held-invalid states and failed saves. Plain dirty/saving flushes on
    // unmount and survives navigation via the module-level queue.
    guardDirty: status === 'invalid' || status === 'error',
  };
}

export default useAutosave;
